import React, { useState } from 'react';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import type { Meeting, MeetingRecap } from '../../types';

interface Props {
  meeting: Meeting;
  recap: MeetingRecap;
  onClose: () => void;
}

type RecapTab = 'summary' | 'actions' | 'transcript';

const SENTIMENT_CONFIG = {
  positive: { icon: 'ti-mood-smile',    color: '#22C55E', bg: '#F0FFF4', label: 'Positive' },
  neutral:  { icon: 'ti-mood-neutral',  color: '#C8973A', bg: '#FFFBEB', label: 'Neutral'  },
  negative: { icon: 'ti-mood-sad',      color: '#EF4444', bg: '#FFF5F5', label: 'Negative' },
};

export default function AiRecapModal({ meeting, recap, onClose }: Props) {
  const { isMobile } = useBreakpoint();
  const [tab, setTab] = useState<RecapTab>('summary');
  const sentiment = SENTIMENT_CONFIG[recap.sentiment] || SENTIMENT_CONFIG.neutral;

  const tabs: { id: RecapTab; label: string; icon: string }[] = [
    { id: 'summary',    label: 'Summary',  icon: 'ti-sparkles'    },
    { id: 'actions',    label: 'Actions',  icon: 'ti-checklist'   },
    { id: 'transcript', label: 'Details',  icon: 'ti-chart-pie'   },
  ];

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,27,45,.6)', zIndex: 700, display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', padding: isMobile ? 0 : '20px' }}
      onClick={onClose}
    >
      <div
        style={{ background: 'white', borderRadius: isMobile ? '20px 20px 0 0' : '16px', width: '100%', maxWidth: isMobile ? '100%' : '560px', maxHeight: isMobile ? '92vh' : '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 32px 80px rgba(0,0,0,.4)', overflow: 'hidden' }}
        onClick={e => e.stopPropagation()}
      >
        {isMobile && <div style={{ width: '36px', height: '4px', borderRadius: '2px', background: 'rgba(15,27,45,.2)', margin: '10px auto 0' }} />}

        {/* Header */}
        <div style={{ padding: '16px 20px', background: 'linear-gradient(135deg, #0F1B2D, #1A2D45)', display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
          <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(200,151,58,.2)', border: '1px solid rgba(200,151,58,.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <i className="ti ti-sparkles" style={{ color: '#C8973A', fontSize: '18px' }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '13px', fontWeight: '700', color: 'white', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>AI Recap — {meeting.title}</div>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,.5)' }}>{meeting.date} · {recap.duration}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,.5)', cursor: 'pointer', fontSize: '22px', padding: '2px', lineHeight: 1 }}>×</button>
        </div>

        {/* Quick stats bar */}
        <div style={{ display: 'flex', borderBottom: '1px solid #E2E8F0', background: '#F8FAFC', flexShrink: 0 }}>
          {/* Sentiment */}
          <div style={{ flex: 1, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: '6px', borderRight: '1px solid #E2E8F0' }}>
            <div style={{ width: '26px', height: '26px', borderRadius: '50%', background: sentiment.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className={`ti ${sentiment.icon}`} style={{ fontSize: '14px', color: sentiment.color }} />
            </div>
            <div>
              <div style={{ fontSize: '10px', color: '#718096', fontWeight: '600' }}>Sentiment</div>
              <div style={{ fontSize: '12px', fontWeight: '700', color: sentiment.color }}>{sentiment.label}</div>
            </div>
          </div>
          {/* Talk time */}
          <div style={{ flex: 2, padding: '10px 12px', borderRight: '1px solid #E2E8F0' }}>
            <div style={{ fontSize: '10px', color: '#718096', fontWeight: '600', marginBottom: '4px' }}>Talk time</div>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <div style={{ flex: 1, height: '6px', borderRadius: '3px', background: '#E2E8F0', overflow: 'hidden' }}>
                <div style={{ width: `${recap.talkTime.rep}%`, height: '100%', background: '#6366F1', borderRadius: '3px' }} />
              </div>
              <span style={{ fontSize: '10px', fontWeight: '700', color: '#6366F1', flexShrink: 0 }}>Rep {recap.talkTime.rep}%</span>
            </div>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '3px' }}>
              <div style={{ flex: 1, height: '6px', borderRadius: '3px', background: '#E2E8F0', overflow: 'hidden' }}>
                <div style={{ width: `${recap.talkTime.client}%`, height: '100%', background: '#C8973A', borderRadius: '3px' }} />
              </div>
              <span style={{ fontSize: '10px', fontWeight: '700', color: '#C8973A', flexShrink: 0 }}>Client {recap.talkTime.client}%</span>
            </div>
          </div>
          {/* Actions count */}
          <div style={{ flex: 1, padding: '10px 12px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ fontSize: '22px', fontWeight: '900', color: '#0F1B2D', lineHeight: 1 }}>{recap.actionItems.length}</div>
            <div style={{ fontSize: '10px', color: '#718096', fontWeight: '600' }}>Actions</div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #E2E8F0', background: 'white', flexShrink: 0 }}>
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{ flex: 1, padding: '10px 4px', border: 'none', background: 'none', fontFamily: 'inherit', fontSize: '11px', fontWeight: '600', cursor: 'pointer', color: tab === t.id ? '#0F1B2D' : '#718096', borderBottom: tab === t.id ? '2px solid #C8973A' : '2px solid transparent', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}
            >
              <i className={`ti ${t.icon}`} style={{ fontSize: '16px' }} />
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>

          {/* SUMMARY TAB */}
          {tab === 'summary' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {/* Summary prose */}
              <div style={{ padding: '14px', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '10px' }}>
                <div style={{ fontSize: '11px', fontWeight: '700', color: '#718096', textTransform: 'uppercase', marginBottom: '8px' }}>Meeting Summary</div>
                <div style={{ fontSize: '13px', color: '#1A202C', lineHeight: 1.7 }}>{recap.summary}</div>
              </div>

              {/* Key points */}
              {recap.keyPoints.length > 0 && (
                <div style={{ padding: '14px', background: '#EEF2FF', border: '1px solid #C7D2FE', borderRadius: '10px' }}>
                  <div style={{ fontSize: '11px', fontWeight: '700', color: '#4338CA', textTransform: 'uppercase', marginBottom: '8px' }}>
                    <i className="ti ti-list-check" style={{ marginRight: '5px' }} />Key Points
                  </div>
                  {recap.keyPoints.map((pt, i) => (
                    <div key={i} style={{ display: 'flex', gap: '8px', fontSize: '12px', color: '#312E81', marginBottom: '6px', lineHeight: 1.5 }}>
                      <span style={{ color: '#6366F1', fontWeight: '700', flexShrink: 0 }}>{i + 1}.</span>
                      {pt}
                    </div>
                  ))}
                </div>
              )}

              {/* Participants */}
              <div style={{ padding: '12px 14px', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '10px' }}>
                <div style={{ fontSize: '11px', fontWeight: '700', color: '#718096', textTransform: 'uppercase', marginBottom: '8px' }}>Participants</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {meeting.participants.map((p, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 10px', background: 'white', border: '1px solid #E2E8F0', borderRadius: '20px' }}>
                      <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: 'var(--navy)', color: 'var(--gold)', fontSize: '8px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{p.ini}</div>
                      <span style={{ fontSize: '12px', fontWeight: '600', color: '#2D3748' }}>{p.name}</span>
                      {p.org && <span style={{ fontSize: '10px', color: '#718096' }}>{p.org}</span>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ACTIONS TAB */}
          {tab === 'actions' && (
            <div>
              {recap.actionItems.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px 0', color: '#718096' }}>
                  <i className="ti ti-checklist" style={{ fontSize: '32px', display: 'block', marginBottom: '8px' }} />
                  <div style={{ fontSize: '13px' }}>No action items found in this meeting.</div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {['rep', 'client'].map(owner => {
                    const items = recap.actionItems.filter(a => a.owner === owner);
                    if (items.length === 0) return null;
                    return (
                      <div key={owner}>
                        <div style={{ fontSize: '11px', fontWeight: '700', color: '#718096', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <i className={`ti ${owner === 'rep' ? 'ti-user-check' : 'ti-building'}`} />
                          {owner === 'rep' ? 'Sales Rep' : 'Client'}
                        </div>
                        {items.map((item, i) => (
                          <div key={i} style={{ display: 'flex', gap: '10px', padding: '11px 13px', background: owner === 'rep' ? '#EEF2FF' : '#FDF4E7', border: `1px solid ${owner === 'rep' ? '#C7D2FE' : '#E8D5A3'}`, borderRadius: '9px', marginBottom: '7px', alignItems: 'flex-start' }}>
                            <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: owner === 'rep' ? '#6366F1' : '#C8973A', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' }}>
                              <i className="ti ti-check" style={{ fontSize: '11px', color: 'white' }} />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: '12px', fontWeight: '600', color: '#1A202C', lineHeight: 1.5 }}>{item.task}</div>
                              {item.due && item.due !== 'TBD' && (
                                <div style={{ fontSize: '10px', color: '#718096', marginTop: '3px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <i className="ti ti-clock" /> Due: {item.due}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* DETAILS TAB */}
          {tab === 'transcript' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* Stats */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                {[
                  { label: 'Duration',    value: recap.duration,                       icon: 'ti-clock',       color: '#6366F1' },
                  { label: 'Key Points',  value: recap.keyPoints.length.toString(),    icon: 'ti-list',        color: '#22C55E' },
                  { label: 'Actions',     value: recap.actionItems.length.toString(),  icon: 'ti-checklist',   color: '#C8973A' },
                  { label: 'Transcript',  value: recap.transcriptAvail ? 'Yes' : 'No', icon: 'ti-file-text',   color: recap.transcriptAvail ? '#22C55E' : '#718096' },
                ].map(s => (
                  <div key={s.label} style={{ padding: '12px', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '10px', display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: s.color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <i className={`ti ${s.icon}`} style={{ fontSize: '16px', color: s.color }} />
                    </div>
                    <div>
                      <div style={{ fontSize: '10px', color: '#718096', fontWeight: '600' }}>{s.label}</div>
                      <div style={{ fontSize: '15px', fontWeight: '800', color: '#0F1B2D' }}>{s.value}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Transcript availability */}
              <div style={{ padding: '12px 14px', background: recap.transcriptAvail ? '#F0FFF4' : '#F8FAFC', border: `1px solid ${recap.transcriptAvail ? '#9AE6B4' : '#E2E8F0'}`, borderRadius: '10px', display: 'flex', gap: '10px', alignItems: 'center' }}>
                <i className={`ti ${recap.transcriptAvail ? 'ti-file-check' : 'ti-file-x'}`} style={{ fontSize: '20px', color: recap.transcriptAvail ? '#22C55E' : '#718096' }} />
                <div>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: recap.transcriptAvail ? '#276749' : '#4A5568' }}>
                    {recap.transcriptAvail ? 'Transcript available' : 'No transcript'}
                  </div>
                  <div style={{ fontSize: '11px', color: '#718096' }}>
                    {recap.transcriptAvail
                      ? 'Full transcript saved to meeting-bot/transcripts/'
                      : 'No speech detected or meeting was too short'}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 20px', borderTop: '1px solid #E2E8F0', background: '#F8FAFC', flexShrink: 0, display: 'flex', gap: '8px' }}>
          <button
            onClick={() => { navigator.clipboard.writeText(recap.summary + '\n\nKey Points:\n' + recap.keyPoints.map(p => `• ${p}`).join('\n')); }}
            style={{ flex: 1, padding: '9px', borderRadius: '8px', border: '1.5px solid #E2E8F0', background: 'white', fontFamily: 'inherit', fontSize: '12px', fontWeight: '600', cursor: 'pointer', color: '#4A5568', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}
          >
            <i className="ti ti-copy" /> Copy Summary
          </button>
          <button
            onClick={onClose}
            style={{ flex: 1, padding: '9px', borderRadius: '8px', border: 'none', background: '#0F1B2D', fontFamily: 'inherit', fontSize: '12px', fontWeight: '700', cursor: 'pointer', color: 'white' }}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
